import React from 'react';
import { TextField, Typography, Box, Alert, Snackbar } from '@mui/material';
import { ConfigItem, AppConfig } from '../../../services/configApi';
import { SectionSaveButtons } from './SectionSaveButtons';
import { useSectionConfig } from '../hooks/useSectionConfig';

interface ProcessQueueConfigProps {
  config: AppConfig;
  items: ConfigItem[];
  disabled?: boolean;
}

const PROCESS_QUEUE_KEYS: (keyof AppConfig)[] = [
  'max_concurrent_jobs',
  'retry_delay_minutes',
];

export const ProcessQueueConfig: React.FC<ProcessQueueConfigProps> = ({
  config,
  items,
  disabled = false,
}) => {
  const {
    currentValues,
    hasChanges,
    saving,
    updateValue,
    saveChanges,
    discardChanges,
  } = useSectionConfig(PROCESS_QUEUE_KEYS, config);

  const [notification, setNotification] = React.useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  // Get config item details for validation and description
  const getConfigItem = (key: string) => {
    return items.find(item => item.key === key);
  };

  const getValidationError = (key: string, value: number): string | null => {
    const item = getConfigItem(key);
    if (!item?.validation_rules) return null;

    const { min, max } = item.validation_rules;
    if (min !== undefined && value < min) {
      return `Must be at least ${min}`;
    }
    if (max !== undefined && value > max) {
      return `Must be at most ${max}`;
    }
    return null;
  };

  const handleChange =
    (key: keyof AppConfig) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(event.target.value, 10);
      if (!Number.isNaN(value)) {
        updateValue(key, value);
      }
    };

  const handleSave = async () => {
    const result = await saveChanges();
    setNotification({
      message: result.message,
      type: result.success ? 'success' : 'error',
    });
  };

  const handleDiscard = () => {
    discardChanges();
    setNotification({
      message: 'Changes discarded',
      type: 'success',
    });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const configFields = [
    {
      key: 'max_concurrent_jobs' as keyof AppConfig,
      label: 'Max Concurrent Jobs',
      description:
        'Maximum concurrent jobs across all process types (script generation, TTS, audio mixing, etc.)',
    },
    {
      key: 'retry_delay_minutes' as keyof AppConfig,
      label: 'Retry Delay (minutes)',
      description: 'Wait time in minutes before retrying a failed job',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {configFields.map(({ key, label, description }) => {
          const item = getConfigItem(key);
          const value = Number(currentValues[key]) || 0;
          const error = getValidationError(key, value);
          const minValue = item?.validation_rules?.min;
          const maxValue = item?.validation_rules?.max;
          return (
            <Box
              key={key}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
            >
              <TextField
                label={label}
                type="number"
                value={value}
                onChange={handleChange(key)}
                disabled={disabled || saving}
                error={!!error}
                sx={{ width: 120 }}
                inputProps={{
                  min: minValue,
                  max: maxValue,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, minWidth: 0, flex: 1 }}
              >
                {error || description}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <SectionSaveButtons
        hasChanges={hasChanges}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
        disabled={disabled}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={closeNotification}
      >
        <Alert
          onClose={closeNotification}
          severity={notification?.type}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
